import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    createInternalUser,
    getInternalUserById,
    getInternalUsers,
    revokeInternalUserAccess,
    updateInternalUser,
} from '@/services/internal-users.service'
import {
    createInternalRole,
    deleteInternalRole,
    getInternalRoleById,
    getInternalRolePermissions,
    getInternalRoles,
    updateInternalRole,
} from '@/services/internal-roles.service'
import type {
    CreateInternalRoleRequest,
    CreateInternalUserRequest,
    InternalRoleDetailResponse,
    InternalRoleFilters,
    InternalRoleListResponse,
    InternalRolePermission,
    InternalRolePermissionsResponse,
    InternalRoleResponse,
    InternalUserDetailResponse,
    InternalUserFilters,
    InternalUserListResponse,
    UpdateInternalRoleRequest,
    UpdateInternalUserRequest,
} from '@/types/internal'

export type InternalUserStatus = 'Active' | 'Inactive' | 'Suspended'

export interface InternalUser {
    id: string
    fullName: string
    email: string
    phoneNumber: string
    gender: 'MALE' | 'FEMALE' | 'M' | 'F'
    role: string
    dateAdded: string
    status: InternalUserStatus
    country?: string
    residentialAddress?: string
    internalRoleId?: string
    internalRoleTitle?: string
    permissions?: InternalRolePermission[]
}

export interface InternalRole {
    id: string
    title: string
    assignedUsers: number
    isActive: boolean
    description?: string
    permissions: InternalRolePermission[]
}

/**
 * Internal users list
 */
export const useInternalUsers = (filters?: InternalUserFilters) => {
    return useQuery<InternalUserListResponse>({
        queryKey: ['internal-users', filters],
        queryFn: () => getInternalUsers(filters),
        staleTime: 1000 * 30,
    })
}

/**
 * Single internal user
 */
export const useInternalUser = (id: string) => {
    return useQuery<InternalUserDetailResponse>({
        queryKey: ['internal-user', id],
        queryFn: () => getInternalUserById(id),
        enabled: !!id,
        staleTime: 1000 * 60,
    })
}

/**
 * Internal roles list
 */
export const useInternalRoles = (filters?: InternalRoleFilters) => {
    return useQuery<InternalRoleListResponse>({
        queryKey: ['internal-roles', filters],
        queryFn: () => getInternalRoles(filters),
        staleTime: 1000 * 30,
    })
}

/**
 * Single internal role
 */
export const useInternalRole = (id: string) => {
    return useQuery<InternalRoleDetailResponse>({
        queryKey: ['internal-role', id],
        queryFn: () => getInternalRoleById(id),
        enabled: !!id,
        staleTime: 1000 * 60,
    })
}

/**
 * All available permissions
 */
export const useInternalRolePermissions = () => {
    return useQuery<InternalRolePermissionsResponse>({
        queryKey: ['internal-role-permissions'],
        queryFn: getInternalRolePermissions,
        staleTime: 1000 * 60 * 5,
    })
}

/**
 * Create internal user
 */
export const useCreateInternalUser = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (data: CreateInternalUserRequest) =>
            createInternalUser(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['internal-users'] })
        },
    })
}

/**
 * Update internal user
 */
export const useUpdateInternalUser = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: UpdateInternalUserRequest
        }) => updateInternalUser(id, data),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['internal-users'] })
            queryClient.invalidateQueries({
                queryKey: ['internal-user', variables.id],
            })
        },
    })
}

/**
 * Revoke access
 */
export const useRevokeInternalUserAccess = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (id: string) => revokeInternalUserAccess(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['internal-users'] })
        },
    })
}

/**
 * Create internal role
 */
export const useCreateInternalRole = () => {
    const queryClient = useQueryClient()

    return useMutation<
        InternalRoleResponse,
        unknown,
        CreateInternalRoleRequest
    >({
        mutationFn: data => createInternalRole(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['internal-roles'] })
        },
    })
}

/**
 * Update internal role
 */
export const useUpdateInternalRole = () => {
    const queryClient = useQueryClient()

    return useMutation<
        InternalRoleResponse,
        unknown,
        { id: string; data: UpdateInternalRoleRequest }
    >({
        mutationFn: ({ id, data }) => updateInternalRole(id, data),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['internal-roles'] })
            queryClient.invalidateQueries({
                queryKey: ['internal-role', variables.id],
            })
        },
    })
}

/**
 * Delete internal role
 */
export const useDeleteInternalRole = () => {
    const queryClient = useQueryClient()

    return useMutation<InternalRoleResponse, unknown, string>({
        mutationFn: id => deleteInternalRole(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['internal-roles'] })
        },
    })
}
